import { useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Button, Card, Checkbox, Label } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import { submitCheckIn, updateCheckIn } from 'Deer/pages/MyData/CheckIns/data';
import { CheckInInstance, CHECK_INS_STATUS_VALUES } from 'Deer/pages/MyData/CheckIns/types';
import { useToast } from 'components/hooks/useToast';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { RichTextEditor2 } from 'uiToolkitV1/RichTextEditor2';
import { TextHeader4 } from 'uiToolkitV1/Typography';

type NextCheckInIntentProps = {
  currentCheckInData: CheckInInstance;
};

export const NextCheckInIntent = ({ currentCheckInData }: NextCheckInIntentProps) => {
  const isCheckInAlreadySubmitted = currentCheckInData.status !== CHECK_INS_STATUS_VALUES.PENDING;
  const [hasAcknowledgedTerms, setHasAcknowledgedTerms] = useState(isCheckInAlreadySubmitted);
  const queryClient = useQueryClient();
  const toast = useToast();

  const handleTextUpdate = async ({
    questionId,
    htmlText,
    plainText,
  }: {
    questionId: number;
    htmlText: string;
    plainText: string;
  }) => {
    await updateCheckIn({
      instanceId: currentCheckInData.checkinUpdateInstanceId,
      questionId,
      checkinUpdateText: plainText,
      checkinUpdateTextHtml: htmlText,
    });
  };

  const handleSubmit = async () => {
    try {
      await submitCheckIn(currentCheckInData.checkinUpdateInstanceId);
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.checkIns.currentCheckIn]);
      toast({
        status: 'success',
        title: 'Check-in submitted successfully',
      });
    } catch (e) {
      toast({
        status: 'error',
        title: e.message ?? 'Something went wrong while submitting check-in',
      });
    }
  };

  return (
    <Card style={{ height: 'calc(100vh - 164px)', padding: 0 }}>
      <Flex flexDir="column" justifyContent="space-between" h="100%" w="100%">
        <Box p="18px">
          <TextHeader4 bold>Enter your responses below questions for checkin</TextHeader4>
          <Flex flexDir="column" gap="12px" mt="24px">
            {currentCheckInData.questions.map((question) => (
              <Flex key={question.id} flexDir="column" gap="4px">
                <TextHeader4 bold>{`Q ${question.description}`}</TextHeader4>
                <RichTextEditor2
                  content={question.responses?.[0]?.checkinUpdateTextHtml ?? ''}
                  placeholder="Enter your response"
                  maxCharLimit={5000}
                  onOutsideClick={(htmlText, plainText) =>
                    handleTextUpdate({
                      questionId: question.id,
                      htmlText,
                      plainText,
                    })
                  }
                  showCharacterCount
                  characterCountProps={{ textAlign: 'right' }}
                  disabled={isCheckInAlreadySubmitted}
                />
              </Flex>
            ))}
          </Flex>
        </Box>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          h="72px"
          w="100%"
          bg="neutral.F5F7F9"
          borderBottomLeftRadius="0.5rem"
          borderBottomEndRadius="0.5rem"
          p="16px"
        >
          <Flex className="rabbit-ds" gap="8px">
            <Checkbox
              checked={hasAcknowledgedTerms}
              id="checkin-accept"
              onCheckedChange={(checked: boolean) => setHasAcknowledgedTerms(checked)}
              disabled={isCheckInAlreadySubmitted}
            />
            <Label htmlFor="checkin-accept">
              Once check-in information shared with their managers, will not be allowed to be edited.
            </Label>
          </Flex>
          <Button disabled={isCheckInAlreadySubmitted || !hasAcknowledgedTerms} onClick={handleSubmit}>
            Submit check-in
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};
